import React from 'react'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { GOLD, GOLD_GLOW, formatTicket } from '../../utils/luckyDraw'
import { LIST_MAX_WIDTH } from './draw'

// Spread into both buttons below, so the disabled state is stated once.
const ACTION_SX = {
  px: 7,
  py: 1.4,
  minWidth: 260,
  fontSize: 'clamp(1rem, 1.6vw, 1.6rem)',
  fontWeight: 800,
  letterSpacing: '0.16em',
  borderRadius: 999,
  color: '#101010',
  '&.Mui-disabled': {
    background: 'rgba(255,255,255,0.14)',
    color: 'rgba(255, 255, 255, 0.55)',
    boxShadow: 'none',
  },
}

const START_SX = {
  ...ACTION_SX,
  background: `linear-gradient(180deg, #ffe285 0%, ${GOLD} 100%)`,
  boxShadow: GOLD_GLOW,
  '&:hover': { background: 'linear-gradient(180deg, #ffeaa0 0%, #ffd45e 100%)' },
}

const STOP_SX = {
  ...ACTION_SX,
  color: '#fff',
  background: 'linear-gradient(180deg, #ff6b5e 0%, #d7372a 100%)',
  boxShadow: '0 0 40px rgba(215, 55, 42, 0.45)',
  '&:hover': { background: 'linear-gradient(180deg, #ff8377 0%, #e8483b 100%)' },
}

// Why the wall has nothing left, per the reason the server found. The two cap fields read almost
// alike in the form, so each names its own place; `drawn` is not a cap and no raise would help.
const CAPPED = 'Every company in this group is at its cap.'
const NO_CARDS = {
  group: `${CAPPED} Raise Max Per Dealer on the prize's group, or add entries.`,
  draw: `${CAPPED} Raise Max Prizes Per Dealer on the draw, or add entries.`,
  both: `${CAPPED} Raise Max Prizes Per Dealer on the draw and Max Per Dealer on its group, or add entries.`,
  drawn: 'Every ticket in this group has already been drawn. Add entries to draw more.',
}

/**
 * Why the message under the wall is there, or null when the wall speaks for itself.
 *
 * `isOperatorOnly` marks the ones the win caps cause, which a projection must never show. Whether
 * the screen is projected is the page's answer, not this component's.
 */
const findNotice = ({ poolError, needsEntryList, needsTicketFields, isExhausted, cap, prize }) => {
  if (poolError) return { text: "Couldn't load the pool.", isError: true }
  if (needsEntryList) {
    return { text: "No entry list yet — upload the sheet on the draw's Entries tab." }
  }
  if (needsTicketFields) {
    return { text: `${prize.name} has no ticket types yet — edit the draw and set them on its group.` }
  }
  if (isExhausted) return { text: NO_CARDS[cap] ?? NO_CARDS.group, isOperatorOnly: true }

  return null
}

/**
 * The prize banner, the START/STOP button, and the one line of explanation under them. The wall
 * and the winners' dialog are rendered by the page, not here — both are full-screen things whose
 * state lives there, and passing them through bought nothing but props.
 *
 * `wall` is the wall element itself, slotted between the banner and the button so this component
 * keeps the column layout without knowing what it is laying out.
 */
const Stage = ({
  prize,
  ticketCount,
  wall,
  slots,
  winners,
  isSweeping,
  canStart,
  onStart,
  onStop,
  needsEntryList,
  isExhausted,
  cap,
  isFullscreen,
  isLoadingPool,
  poolError,
  onRetryPool,
}) => {
  // No ticket types on the prize's group means an empty wall.
  const needsTicketFields = Boolean(prize) && (prize.ticketFields ?? []).length === 0
  const found = findNotice({ poolError, needsEntryList, needsTicketFields, isExhausted, cap, prize })
  const notice = found?.isOperatorOnly && isFullscreen ? null : found
  // Reads "now drawing" right through the press, and only names the prize as won once the wall
  // has stopped and the winners are in.
  const hasLanded = winners.length > 0 && !isSweeping
  const caption = hasLanded && winners.length > 1 ? 'WINNERS OF' : 'WINNER OF'
  // No prize yet means the draw itself is loading; the page redirects a draw that has none.
  const isLoadingWall = !prize || isLoadingPool
  // A failed load is named under the button. Never a cap: a capped company keeps its cards.
  const isEmpty = !isLoadingWall && !poolError

  return (
    <Box display='flex' flexDirection='column' height='100%' gap={{ xs: 1.5, md: 2.5 }}>
      <Box textAlign='center'>
        <Typography
          sx={{
            color: 'rgba(255, 255, 255, 0.55)',
            letterSpacing: '0.5em',
            textAlign: 'center',
            fontSize: 'clamp(0.7rem, 1vw, 1rem)',
          }}
        >
          {hasLanded ? caption : 'NOW DRAWING'}
        </Typography>
        <Typography
          sx={{
            color: GOLD,
            fontWeight: 700,
            lineHeight: 1.1,
            fontSize: 'clamp(1.4rem, 3vw, 3rem)',
            textShadow: GOLD_GLOW,
          }}
        >
          {prize?.name ?? '—'}
        </Typography>
        {prize && (
          <Typography
            sx={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: 'clamp(0.8rem, 1.1vw, 1.2rem)' }}
          >
            {prize.remaining} of {prize.quantity} remaining ·{' '}
            {formatTicket(ticketCount)} tickets in the draw
            {slots > 1 ? ` · drawing ${slots} at once` : ''}
          </Typography>
        )}
      </Box>

      {/* Holds the wall's space while it loads or has no cards, so START does not jump up. */}
      {wall || (
        <Box flex={1} minHeight={0} display='flex' alignItems='center' justifyContent='center'>
          {isLoadingWall && <CircularProgress sx={{ color: GOLD }} />}
          {isEmpty && (
            <Typography
              sx={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: 'clamp(0.8rem, 1.1vw, 1.1rem)' }}
            >
              No tickets for this prize yet.
            </Typography>
          )}
        </Box>
      )}

      <Box display='flex' flexDirection='column' alignItems='center' gap={1}>
        {isSweeping ? (
          <Button onClick={onStop} sx={STOP_SX}>
            STOP
          </Button>
        ) : (
          <Button onClick={onStart} disabled={!canStart} sx={START_SX}>
            START
          </Button>
        )}

        {notice && (
          <Box
            display='flex'
            gap={1}
            alignItems='center'
            justifyContent='center'
            sx={{ maxWidth: LIST_MAX_WIDTH, textAlign: 'center' }}
          >
            <Typography
              sx={{
                color: notice.isError ? '#ff8a80' : GOLD,
                fontSize: 'clamp(0.8rem, 1.1vw, 1.1rem)',
              }}
            >
              {notice.text}
            </Typography>
            {notice.isError && (
              <Button size='small' onClick={onRetryPool} sx={{ color: GOLD }}>
                RETRY
              </Button>
            )}
          </Box>
        )}

        {/* Only the wait is worth saying, since START is dark until the pool lands. */}
        {!notice && isLoadingPool && (
          <Typography
            sx={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: 'clamp(0.8rem, 1.1vw, 1.1rem)' }}
          >
            Loading the pool…
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default Stage
